import {
  useEffect,
  useState,
} from "react";

import {
  FaSearch,
  FaCalendarAlt,
  FaSyncAlt,
  FaChartLine,
  FaFutbol,
} from "react-icons/fa";


import AdminPage from "./components/AdminPage";
import AdminCard from "./components/AdminCard";
import AdminTable from "./components/AdminTable";
import PrimaryButton from "./components/PrimaryButton";


import {
  scanValueBets,
  scanTodayValueBets,
} from "./api/scansApi";


export default function Scans() {

  const [date, setDate] =
    useState(getTomorrowDate());

  const [scan, setScan] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");


  /*
   * ---------------------------------------------------------
   * INITIAL SCAN
   * ---------------------------------------------------------
   *
   * We don't automatically execute it here.
   *
   * The user can select the date and press "Run Scan".
   */


  /*
   * ---------------------------------------------------------
   * SCAN SELECTED DATE
   * ---------------------------------------------------------
   */

  const handleScan =
    async () => {

      if (!date) {
        return;
      }


      setLoading(true);
      setError("");


      try {

        const data =
          await scanValueBets(date);


        setScan(data);

      }

      catch (error) {

        console.error(
          "Error running value scan:",
          error
        );


        setError(
          error?.response?.data?.message ||
          "Failed to run value scan."
        );

        setScan(null);

      }

      finally {

        setLoading(false);

      }

    };


  /*
   * ---------------------------------------------------------
   * TODAY
   * ---------------------------------------------------------
   */

  const handleToday =
    async () => {

      setLoading(true);
      setError("");


      try {

        const data =
          await scanTodayValueBets();


        setDate(
          data?.date ||
          getTodayDate()
        );


        setScan(data);

      }

      catch (error) {

        console.error(
          "Error running today's scan:",
          error
        );


        setError(
          error?.response?.data?.message ||
          "Failed to run today's scan."
        );

        setScan(null);

      }

      finally {

        setLoading(false);

      }

    };


  /*
   * ---------------------------------------------------------
   * TOMORROW
   * ---------------------------------------------------------
   */

  const handleTomorrow =
    () => {

      const tomorrow =
        getTomorrowDate();


      setDate(tomorrow);

    };


  /*
   * ---------------------------------------------------------
   * VALUE BETS
   * ---------------------------------------------------------
   */

  const valueBets =
    scan?.results || [];


  const scanned =
    scan?.matches?.scanned || 0;


  const withValueBets =
    scan?.matches?.withValueBets || 0;


  return (

    <AdminPage
      title="Scans"
      subtitle="Scan upcoming matches for value bets."
    >

      {/* ------------------------------------------------- */}
      {/* SCAN CONTROLS */}
      {/* ------------------------------------------------- */}

      <AdminCard className="p-6 mb-6">

        <div
          className="
            flex
            flex-col
            lg:flex-row
            lg:items-end
            gap-4
          "
        >

          {/* DATE */}

          <div className="flex-1">

            <label
              className="
                block
                text-sm
                text-gray-400
                mb-2
              "
            >
              Scan date
            </label>


            <div className="relative">

              <FaCalendarAlt
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-gray-500
                  pointer-events-none
                "
              />


              <input
                type="date"
                value={date}
                onChange={(e) =>
                  setDate(e.target.value)
                }
                className="
                  w-full
                  bg-black/30
                  border
                  border-green-500/20
                  rounded-lg
                  px-10
                  py-3
                  text-white
                  outline-none
                  focus:border-green-400/50
                  transition
                "
              />

            </div>

          </div>


          {/* QUICK DATE BUTTONS */}

          <div className="flex gap-2">

            <button
              type="button"
              onClick={() =>
                setDate(getTodayDate())
              }
              className="
                px-4
                py-3
                rounded-lg
                bg-white/5
                hover:bg-white/10
                border
                border-white/10
                text-gray-300
                transition
              "
            >
              Today
            </button>


            <button
              type="button"
              onClick={handleTomorrow}
              className="
                px-4
                py-3
                rounded-lg
                bg-white/5
                hover:bg-white/10
                border
                border-white/10
                text-gray-300
                transition
              "
            >
              Tomorrow
            </button>

          </div>


          {/* SCAN BUTTON */}

          <PrimaryButton
            onClick={handleScan}
            disabled={loading || !date}
          >

            {loading ? (

              <div
                className="
                  flex
                  items-center
                  gap-2
                "
              >

                <FaSyncAlt
                  className="animate-spin"
                />

                Scanning...

              </div>

            ) : (

              <div
                className="
                  flex
                  items-center
                  gap-2
                "
              >

                <FaSearch />

                Run Scan

              </div>

            )}

          </PrimaryButton>


          {/* TODAY SCAN */}

          <button
            type="button"
            onClick={handleToday}
            disabled={loading}
            className="
              px-4
              py-3
              rounded-lg
              bg-green-500/10
              hover:bg-green-500/20
              border
              border-green-500/20
              text-green-400
              transition
              disabled:opacity-50
            "
          >

            Today Scan

          </button>

        </div>


        {/* ERROR */}

        {error && (

          <div
            className="
              mt-4
              p-4
              rounded-lg
              bg-red-900/20
              border
              border-red-500/20
              text-red-400
            "
          >
            {error}
          </div>

        )}

      </AdminCard>


      {/* ------------------------------------------------- */}
      {/* SUMMARY */}
      {/* ------------------------------------------------- */}

      {scan && (

        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-3
            gap-4
            mb-6
          "
        >

          {/* SCANNED */}

          <AdminCard className="p-5">

            <div
              className="
                flex
                items-center
                justify-between
              "
            >

              <div>

                <p className="text-sm text-gray-400">
                  Matches scanned
                </p>

                <p
                  className="
                    text-3xl
                    font-bold
                    text-white
                    mt-1
                  "
                >
                  {scanned}
                </p>

              </div>


              <div
                className="
                  p-3
                  rounded-xl
                  bg-blue-500/10
                  text-blue-400
                "
              >
                <FaFutbol />

              </div>

            </div>

          </AdminCard>


          {/* VALUE BETS */}

          <AdminCard className="p-5">

            <div
              className="
                flex
                items-center
                justify-between
              "
            >

              <div>

                <p className="text-sm text-gray-400">
                  Value matches
                </p>

                <p
                  className="
                    text-3xl
                    font-bold
                    text-green-400
                    mt-1
                  "
                >
                  {withValueBets}
                </p>

              </div>


              <div
                className="
                  p-3
                  rounded-xl
                  bg-green-500/10
                  text-green-400
                "
              >
                <FaChartLine />

              </div>

            </div>

          </AdminCard>


          {/* PRESET */}

          <AdminCard className="p-5">

            <div>

              <p className="text-sm text-gray-400">
                Algorithm preset
              </p>

              <p
                className="
                  text-xl
                  font-semibold
                  text-white
                  mt-2
                "
              >
                {scan?.results?.[0]?.preset?.name ||
                  "Unknown"}
              </p>

              {scan?.results?.[0]?.preset?.id && (

                <p className="text-xs text-gray-500 mt-1">

                  ID:{" "}
                  {scan.results[0].preset.id}

                </p>

              )}

            </div>

          </AdminCard>

        </div>

      )}


      {/* ------------------------------------------------- */}
      {/* VALUE BET TABLE */}
      {/* ------------------------------------------------- */}

      {scan && (

        <AdminTable
          headers={[
            "Time",
            "Match",
            "League",
            "Market",
            "Probability",
            "Odds",
            "EV",
            "Confidence",
            "Rating",
          ]}
          loading={loading}
          empty={!valueBets.length}
          emptyMessage="No value bets found for this date."
        >

          {valueBets.map(
            (result) => {

              /*
               * A match can theoretically have
               * multiple value bets.
               *
               * We therefore create one row
               * for every value bet.
               */

              return result.valueBets.map(
                (bet, betIndex) => (

                  <ValueBetRow
                    key={`
                      ${result.match.id}-
                      ${betIndex}
                    `}
                    result={result}
                    bet={bet}
                  />

                )
              );

            }
          )}

        </AdminTable>

      )}

    </AdminPage>

  );

}


/*
 * ============================================================
 * VALUE BET ROW
 * ============================================================
 */

function ValueBetRow({
  result,
  bet,
}) {

  const match =
    result.match;


  const recommendation =
    bet.recommendation;


  const isHighValue =
    recommendation === "high_value";


  return (

    <tr
      className="
        border-t
        border-green-500/10
        hover:bg-white/5
        transition
      "
    >

      {/* TIME */}

      <td className="p-4 whitespace-nowrap">

        <span
          className="
            text-white
            font-medium
          "
        >
          {formatTime(match.date)}
        </span>

      </td>


      {/* MATCH */}

      <td className="p-4">

        <div className="min-w-[220px]">

          <div
            className="
              text-white
              font-semibold
            "
          >
            {match.home}
          </div>

          <div
            className="
              text-gray-500
              text-xs
              my-0.5
            "
          >
            vs
          </div>

          <div
            className="
              text-white
              font-semibold
            "
          >
            {match.away}
          </div>

        </div>

      </td>


      {/* LEAGUE */}

      <td className="p-4">

        <div className="min-w-[180px]">

          <div
            className="
              text-gray-300
              text-sm
            "
          >
            {match.league}
          </div>

          <div
            className="
              text-gray-600
              text-xs
              mt-1
            "
          >
            {match.country}
          </div>

        </div>

      </td>


      {/* MARKET */}

      <td className="p-4">

        <span
          className="
            inline-flex
            items-center
            px-3
            py-1.5
            rounded-lg
            bg-blue-500/10
            border
            border-blue-500/20
            text-blue-300
            text-sm
            font-medium
            whitespace-nowrap
          "
        >
          {formatMarket(bet.market)}

        </span>

      </td>


      {/* PROBABILITY */}

      <td className="p-4">

        <span
          className="
            text-green-300
            font-bold
          "
        >
          {formatNumber(bet.probability)}%

        </span>

      </td>


      {/* ODDS */}

      <td className="p-4">

        <div>

          <span
            className="
              text-white
              font-bold
            "
          >
            {formatNumber(
              bet.odd,
              2
            )}

          </span>

          {bet.bookmaker && (

            <div
              className="
                text-xs
                text-gray-500
                mt-1
              "
            >
              {bet.bookmaker}
            </div>

          )}

        </div>

      </td>


      {/* EV */}

      <td className="p-4">

        <span
          className="
            text-green-400
            font-bold
          "
        >
          +{formatNumber(
            bet.expectedValue
          )}

        </span>

      </td>


      {/* CONFIDENCE */}

      <td className="p-4">

        <span
          className="
            text-gray-200
            font-semibold
          "
        >
          {formatNumber(
            bet.confidenceScore
          )}

        </span>

      </td>


      {/* RECOMMENDATION */}

      <td className="p-4">

        <span
          className={`
            inline-flex
            items-center
            px-3
            py-1.5
            rounded-full
            text-xs
            font-semibold
            whitespace-nowrap

            ${
              isHighValue
                ? `
                  bg-green-500/20
                  text-green-400
                  border
                  border-green-500/20
                `
                : `
                  bg-green-500/20
                  text-green-400
                  border
                  border-green-500/20
                `
            }
          `}
        >

          {isHighValue
            ? "HIGH VALUE"
            : "VALUE"}

        </span>

      </td>

    </tr>

  );

}


/*
 * ============================================================
 * HELPERS
 * ============================================================
 */

function formatMarket(
  market
) {

  const markets = {

    over_05:
      "Over 0.5",

    over_15:
      "Over 1.5",

    over_25:
      "Over 2.5",

    over_35:
      "Over 3.5",

    under_15:
      "Under 1.5",

    under_25:
      "Under 2.5",

    under_35:
      "Under 3.5",

    btts:
      "BTTS",

    home_win:
      "Home Win",

    draw:
      "Draw",

    away_win:
      "Away Win",

  };


  return (
    markets[market] ||
    market
      ?.replaceAll("_", " ")
      ?.replace(/\b\w/g, char =>
        char.toUpperCase()
      ) ||
    "-"
  );

}


function formatNumber(
  value,
  decimals = 2
) {

  const number =
    Number(value);


  if (
    !Number.isFinite(number)
  ) {

    return "-";

  }


  return number.toFixed(
    decimals
  );

}


function formatTime(
  date
) {

  if (!date) {
    return "-";
  }


  return new Intl.DateTimeFormat(
    undefined,
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(
    new Date(date)
  );

}


function getTodayDate() {

  const date =
    new Date();


  return date
    .toISOString()
    .slice(0, 10);

}


function getTomorrowDate() {

  const date =
    new Date();


  date.setDate(
    date.getDate() + 1
  );


  return date
    .toISOString()
    .slice(0, 10);

}